import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { ButtonLink } from "../components/ui/Button.tsx";
import {
  getCheckoutSession,
  ApiError,
  type CheckoutSession,
} from "../lib/api.ts";
import { useCart, formatMoney } from "../lib/cart.tsx";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; session: CheckoutSession };

export default function ProductThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") ?? "";
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({
        status: "error",
        message: "No session id — did Stripe redirect correctly?",
      });
      return;
    }
    let cancelled = false;
    getCheckoutSession(sessionId)
      .then((session) => {
        if (cancelled) return;
        setState({ status: "ready", session });
        if (session.paymentStatus === "paid") {
          clear();
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof ApiError ? err.message : "Unknown error",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center md:px-10 md:py-32">
      <section className="marble-panel relative overflow-hidden px-8 py-20 md:px-14 md:py-24">
        <Feather
          className="pointer-events-none absolute top-1/2 left-1/2 h-[520px] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.07]"
          style={{ animation: "var(--animate-fade-in)" }}
        />

        <div className="relative">
          <span
            className="eyebrow"
            style={{ animation: "var(--animate-fade-up)" }}
          >
            {state.status === "ready" && state.session.paymentStatus === "paid"
              ? "Order Received"
              : "Checkout"}
          </span>

          <h1
            className="mt-6 font-serif text-4xl leading-[1.05] text-[var(--color-emerald-deep)] md:text-6xl"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "140ms",
            }}
          >
            {state.status === "loading" ? (
              <>Confirming your order…</>
            ) : state.status === "error" ? (
              <>Something went quiet.</>
            ) : state.session.paymentStatus === "paid" ? (
              <>
                Thank you,{" "}
                <em className="text-gold-leaf font-serif italic">truly.</em>
              </>
            ) : (
              <>We couldn't confirm payment.</>
            )}
          </h1>

          <Divider className="my-10" />

          {state.status === "loading" ? (
            <p className="font-serif text-lg italic text-[var(--color-stone)]">
              reaching Stripe for your receipt…
            </p>
          ) : state.status === "error" ? (
            <p className="text-sm text-[var(--color-stone)]">{state.message}</p>
          ) : state.session.paymentStatus === "paid" ? (
            <div className="flex flex-col gap-6">
              <p className="font-serif text-xl italic text-[var(--color-emerald-deep)]">
                Your order is on its way to the atelier. A receipt has been
                sent to{" "}
                <span className="text-[var(--color-amber)]">
                  {state.session.email ?? "your email"}
                </span>
                .
              </p>

              <ul className="mx-auto flex w-full max-w-md flex-col gap-3 rounded border border-[var(--color-rule)] bg-[var(--color-warm-white)]/60 px-8 py-6 text-left">
                {state.session.items.map((item, i) => (
                  <li
                    key={`${item.slug}-${i}`}
                    className="flex items-baseline justify-between border-b border-[var(--color-rule)]/50 pb-3 last:border-b-0 last:pb-0"
                  >
                    <div>
                      <p className="font-serif text-[var(--color-emerald-deep)]">
                        {item.name}
                      </p>
                      <p className="mt-0.5 text-[0.62rem] uppercase tracking-[0.18em] text-[var(--color-stone)]">
                        qty {item.quantity}
                      </p>
                    </div>
                    <span className="font-serif text-sm italic text-[var(--color-charcoal)]/80">
                      {formatMoney(
                        item.unitAmount * item.quantity,
                        item.currency,
                      )}
                    </span>
                  </li>
                ))}
                {state.session.amountTotal != null ? (
                  <li className="mt-2 flex items-baseline justify-between border-t border-[var(--color-gold)]/40 pt-4">
                    <span className="text-[0.62rem] uppercase tracking-[0.22em] text-[var(--color-amber)]">
                      Total
                    </span>
                    <span className="font-serif text-xl text-[var(--color-emerald-deep)]">
                      {formatMoney(
                        state.session.amountTotal,
                        state.session.currency ?? "usd",
                      )}
                    </span>
                  </li>
                ) : null}
              </ul>

              <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--color-stone)]">
                Order · {state.session.sessionId.slice(0, 22)}… · Stripe
                Stripe secure payment
              </p>
            </div>
          ) : (
            <p className="font-serif text-lg italic text-[var(--color-stone)]">
              Status: {state.session.paymentStatus ?? "unknown"} — please try
              again.
            </p>
          )}
        </div>
      </section>

      <div className="mt-12 flex flex-col items-center gap-4">
        <ButtonLink to="/collection">Return to the Collection</ButtonLink>
        <Link
          to="/create"
          className="mt-4 text-[0.7rem] uppercase tracking-[0.16em] text-[var(--color-stone)] hover:text-[var(--color-emerald)]"
        >
          or compose a horoscope candle →
        </Link>
      </div>
    </div>
  );
}
